import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Shield, Trash2, MessageSquare, X, RefreshCw, Wand2, EyeOff, MessageCircle, FileText, Sparkles } from "lucide-react";

/**
 * AdminContentFlags — anti-scam review queue. Surfaces every pending flag
 * with the LLM's reasoning + severity + categories + a snippet of the
 * source content. Admin chooses Dismiss / Warn / Remove. High-severity
 * flags are auto-hidden by the sweep — dismissing them restores the content.
 */
const SEV_COLOR = { high: "text-orange-300", medium: "text-violet-200", low: "text-foreground/65" };
const SOURCE_ICON = { service: Sparkles, post: FileText, message: MessageCircle };
const SOURCE_LABEL = { service: "listing", post: "post", message: "direct message" };

export default function AdminContentFlags() {
  const [flags, setFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        api.get("/admin/content-flags?status=pending"),
        api.get("/admin/content-flags/stats"),
      ]);
      setFlags(f.data.flags || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sweepNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/content-flags/sweep-now?limitServices=20&limitPosts=20&limitMessages=20");
      const total = (data.servicesScanned || 0) + (data.postsScanned || 0) + (data.messagesScanned || 0);
      const flagged = (data.servicesFlagged || 0) + (data.postsFlagged || 0) + (data.messagesFlagged || 0);
      toast.success(`Scanned ${total} items · ${flagged} flagged`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  const resolve = async (fid, decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/content-flags/${fid}/resolve`, { decision });
      const msg = decision === "dismiss" ? "dismissed (content restored if auto-hidden)"
        : decision === "warn" ? "owner warned"
        : "content removed";
      toast.success(`Flag ${msg}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-content-flags">
      <div>
        <div className="label-mystic">Trust safety</div>
        <h2 className="font-serif text-3xl mt-1">Content Flags</h2>
        <p className="text-foreground/70 mt-2 max-w-2xl">
          Claude scans active service listings (every 7 days), recent public posts,
          and direct messages (last 24h) for coercive language, false guarantees,
          impersonation, and other policy violations. High-severity flags are
          auto-hidden immediately. Each flag below is awaiting your call.
        </p>
      </div>

      {stats && (
        <div className="space-y-3" data-testid="content-flag-stats">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 parchment p-4">
            <Stat label="Pending" value={stats.pending} color="text-orange-300" />
            <Stat label="Auto-hidden" value={stats.autoHiddenPending ?? 0} color="text-orange-300" />
            <Stat label="Dismissed" value={stats.dismissed} />
            <Stat label="Owner Warned" value={stats.warned} />
            <Stat label="Removed" value={stats.removed} color="text-orange-300" />
          </div>
          {stats.bySource && (
            <div className="flex flex-wrap gap-3 text-xs text-foreground/65">
              <span className="label-mystic">Pending by source:</span>
              <span data-testid="cf-by-source-service">Listings · <b className="text-foreground/85">{stats.bySource.service}</b></span>
              <span data-testid="cf-by-source-post">Posts · <b className="text-foreground/85">{stats.bySource.post}</b></span>
              <span data-testid="cf-by-source-message">Chats · <b className="text-foreground/85">{stats.bySource.message}</b></span>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={load} disabled={busy}
          className="inline-flex items-center gap-1.5 border border-secondary/30 text-secondary px-4 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
          data-testid="cf-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
        <button onClick={sweepNow} disabled={busy}
          className="inline-flex items-center gap-1.5 bg-violet-300 text-background px-4 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
          data-testid="cf-sweep-btn">
          <Wand2 size={12} /> {busy ? "Scanning..." : "Run scan now"}
        </button>
      </div>

      {loading && <div className="text-foreground/55 italic">Loading…</div>}

      {!loading && flags.length === 0 && (
        <div className="parchment p-12 text-center text-foreground/55 italic font-serif text-xl" data-testid="cf-empty">
          No pending flags. Trust safety is clean.
        </div>
      )}

      {flags.map((f) => {
        const SrcIcon = SOURCE_ICON[f.source] || AlertTriangle;
        const srcLabel = SOURCE_LABEL[f.source] || f.source;
        return (
        <div key={f.id} className="parchment p-5 space-y-3" data-testid={`flag-${f.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="inline-flex items-center gap-2 flex-wrap">
              <AlertTriangle size={14} className={SEV_COLOR[f.severity] || "text-foreground/55"} />
              <span className={`text-[11px] uppercase tracking-[0.16em] ${SEV_COLOR[f.severity] || "text-foreground/55"}`}>
                {f.severity} severity
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-foreground/65">
                <SrcIcon size={11} /> {srcLabel}
              </span>
              {f.autoHidden && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-orange-400/15 text-orange-300 uppercase tracking-wider" data-testid={`flag-autohidden-${f.id}`}>
                  <EyeOff size={10} /> auto-hidden
                </span>
              )}
              {(f.categories || []).map((c) => (
                <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-orange-400/10 text-orange-300 uppercase tracking-wider">
                  {c.replace("_", " ")}
                </span>
              ))}
            </div>
            <span className="text-[10px] text-foreground/40">{new Date(f.createdAt).toLocaleString()}</span>
          </div>

          {f.owner && (
            <div className="text-xs text-foreground/65">
              Owner: <a href={`/profile/${f.owner.id}`} className="text-secondary hover:underline">{f.owner.name}</a>
              <span className="text-foreground/40 ml-2">{f.owner.email}</span>
            </div>
          )}

          <div className="border-l-2 border-orange-400/40 pl-3 text-sm text-foreground/80 italic" data-testid={`flag-snippet-${f.id}`}>
            "{f.snippet}"
          </div>

          <div className="text-xs text-foreground/65" data-testid={`flag-reasoning-${f.id}`}>
            <span className="label-mystic">LLM reasoning · </span>
            {f.reasoning || <span className="italic text-foreground/40">(none)</span>}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => resolve(f.id, "dismiss")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-secondary/30 text-foreground/75 px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] disabled:opacity-50"
              data-testid={`flag-dismiss-${f.id}`}>
              <X size={11} /> {f.autoHidden ? "Dismiss & restore" : "Dismiss"}
            </button>
            <button onClick={() => resolve(f.id, "warn")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-violet-300/40 text-violet-200 px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] disabled:opacity-50"
              data-testid={`flag-warn-${f.id}`}>
              <MessageSquare size={11} /> Warn owner
            </button>
            <button onClick={() => resolve(f.id, "remove")} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-orange-400 text-background px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] disabled:opacity-50"
              data-testid={`flag-remove-${f.id}`}>
              <Trash2 size={11} /> Remove content
            </button>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, color = "text-foreground/85" }) {
  return (
    <div>
      <div className="label-mystic">{label}</div>
      <div className={`font-serif text-2xl ${color}`}>{value}</div>
    </div>
  );
}
